import { store } from "../reduxStore";
import { ga4Event, sendVirtualPageView } from "./ga4/GoogleAnalyticsEvents";

// GA4 replacement for the retired Universal Analytics (react-ga / UA-107501180-2,
// which stopped processing in 2023). The public API (initialise / pageView /
// logEvent) is unchanged, so the ~55 existing call sites keep working; only the
// transport is now GA4 via gtag.js. Every event carries the cross-app linkage
// params (reservation_id / customer_id / app) so engagement unifies with the
// ecommerce events and with the www booking purchase. Production-gated and
// console-logged otherwise (handled inside ga4Event).

// Action labels like "PIN Success" / "Briefing to Monitor Phase" → snake_case GA4
// event names ("pin_success", "briefing_to_monitor_phase"). GA4 event names must be
// <=40 chars, alphanumeric + underscores.
const toSnakeEvent = (action: string): string =>
  (action || "event")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "event";

export const initialise = () => {
  // No-op: gtag.js is loaded statically in public/index.html now (prod hostnames
  // only). Kept as an export so the existing App.tsx call site doesn't change.
};

export const pageView = (url: string) => {
  sendVirtualPageView(url);
};

export const logEvent = (
  action: string,
  altLabel?: string,
  value?: number,
  error?: string,
  extraParams?: Record<string, string | number | boolean>
) => {
  const state = store.getState().chauffeurReducer;
  const res = state.reservation;

  let label = "-1";
  let userId = "-1";
  let firstTimeRes: string | undefined;
  if (res) {
    label = String(res.id);
    firstTimeRes = res.identifyMethod === 0 ? "true" : "false";
    userId = String(res.customerId);
  } else if (state.user) {
    userId = String(state.user.id);
  }

  const params: Record<string, any> = {
    app: "chauffeur",
    event_label: altLabel || label,
    reservation_id: res ? String(res.id) : undefined,
    customer_id: userId !== "-1" ? userId : undefined,
    phase: state.phase || undefined,
    launcher_mode: !!sessionStorage.getItem("L-M"),
    is_first_reservation: firstTimeRes,
    pwa:
      typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches === true,
  };
  if (value) params.value = value;
  // Callers pass whatever their catch block caught, so coerce: GA4 param values are
  // strings and are truncated at 100 chars anyway.
  if (error) params.error_string = String((error as any)?.message ?? error).slice(0, 100);
  if (extraParams) Object.assign(params, extraParams);

  ga4Event(toSnakeEvent(action), params);
};
